/**
 * Authentification et rôle (RG-01 à RG-07). Propagation des rôles sans
 * reconnexion (RG-03) : écoute de users/{uid}.claimsUpdatedAt, puis
 * getIdToken(true) si le jeton est antérieur au changement.
 */
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
  updateProfile,
  type User,
} from 'firebase/auth'
import { Timestamp, clearIndexedDbPersistence, doc, onSnapshot, terminate, type Unsubscribe } from 'firebase/firestore'
import { auth, db } from '@/firebase'
import type { Role } from '@/types'

const RANK: Record<Role, number> = { '': 0, member: 1, editor: 2, superadmin: 3 }

function asRole(v: unknown): Role {
  return v === 'member' || v === 'editor' || v === 'superadmin' ? v : ''
}

/** Traduction des erreurs Firebase Authentication (RG-05, RG-73). */
export function authErrorMessage(e: unknown): string {
  const code = (e as { code?: string } | null)?.code ?? ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'E-mail ou mot de passe incorrect.'
    case 'auth/invalid-email':
      return 'Cette adresse e-mail n’est pas valide.'
    case 'auth/email-already-in-use':
      return 'Un compte existe déjà avec cette adresse e-mail.'
    case 'auth/weak-password':
      return 'Le mot de passe doit faire au moins 8 caractères.'
    case 'auth/too-many-requests':
      return 'Trop de tentatives. Réessayez dans quelques minutes.'
    case 'auth/network-request-failed':
      return 'Connexion impossible. Vérifiez votre accès à Internet.'
    case 'auth/user-disabled':
      return 'Ce compte a été désactivé.'
    case 'auth/account-exists-with-different-credential':
      return 'Un compte existe déjà avec cette adresse, avec un autre mode de connexion.'
    default:
      return 'La connexion a échoué. Réessayez.'
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const role = ref<Role>('')
  const displayName = ref('')
  const ready = ref(false)
  const tokenIssuedAt = ref<number>(0)
  /** UID dont le rôle est chargé : évite de router avant la lecture des claims. */
  const loadedUid = ref<string | null>(null)

  const isSignedIn = computed(() => user.value !== null)
  const isPending = computed(() => user.value !== null && role.value === '')
  const hasRole = (min: Exclude<Role, ''>) => RANK[role.value] >= RANK[min]
  const isMember = computed(() => hasRole('member'))
  const isEditor = computed(() => hasRole('editor'))
  const isSuperadmin = computed(() => role.value === 'superadmin')
  const initial = computed(() => (displayName.value || user.value?.email || '?').trim().charAt(0).toUpperCase())

  let unsubUserDoc: Unsubscribe | null = null
  let readyResolve: (() => void) | null = null
  const readyPromise = new Promise<void>((r) => (readyResolve = r))

  async function loadClaims(force = false): Promise<void> {
    const u = auth.currentUser
    if (!u) return
    const res = await u.getIdTokenResult(force)
    role.value = asRole(res.claims.role)
    tokenIssuedAt.value = new Date(res.issuedAtTime).getTime()
    displayName.value = u.displayName ?? (typeof res.claims.name === 'string' ? res.claims.name : '')
  }

  function watchUserDoc(uid: string) {
    unsubUserDoc?.()
    unsubUserDoc = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const at = snap.get('claimsUpdatedAt') as unknown
        if (!(at instanceof Timestamp)) return
        // Jeton antérieur au changement de rôle : rafraîchissement forcé (RG-03).
        // La marge d'une seconde couvre l'arrondi de iat (en secondes).
        if (at.toMillis() > tokenIssuedAt.value - 1000) void loadClaims(true)
      },
      () => undefined, // document illisible (déconnexion en cours) : sans effet
    )
  }

  function init(): Promise<void> {
    onAuthStateChanged(auth, async (u) => {
      user.value = u
      if (u) {
        try {
          await loadClaims()
        } catch {
          role.value = ''
        }
        loadedUid.value = u.uid
        watchUserDoc(u.uid)
      } else {
        loadedUid.value = null
        unsubUserDoc?.()
        unsubUserDoc = null
        role.value = ''
        displayName.value = ''
        tokenIssuedAt.value = 0
      }
      ready.value = true
      readyResolve?.()
    })
    return readyPromise
  }

  /** Résolue quand l'utilisateur connecté et son rôle sont chargés. */
  function settled(): Promise<void> {
    return new Promise((resolve) => {
      const stop = watch(
        () => [loadedUid.value, auth.currentUser?.uid] as const,
        ([loaded, current]) => {
          if (current !== undefined && loaded === current) {
            queueMicrotask(() => stop())
            resolve()
          }
        },
        { immediate: true },
      )
    })
  }

  // --- Actions de connexion (RG-04 à RG-06) ---

  async function signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })
    try {
      await signInWithPopup(auth, provider)
    } catch (e) {
      const code = (e as { code?: string }).code
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(auth, provider)
        return
      }
      throw e
    }
  }

  async function signInWithEmail(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(auth, email.trim(), password)
  }

  /** Inscription : le nom saisi devient le nom d'emprunteur (RG-05, RG-07). */
  async function signUp(name: string, email: string, password: string): Promise<void> {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password)
    await updateProfile(cred.user, { displayName: name.trim() })
    displayName.value = name.trim()
    await cred.user.getIdToken(true) // le claim `name` figure dans le jeton envoyé à l'API
  }

  /** Seul e-mail envoyé : la réinitialisation native de Firebase (RG-06). */
  async function resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email.trim(), { url: `${location.origin}/connexion` })
  }

  /** Déconnexion : purge du cache local (appareil partagé), puis rechargement. */
  async function signOut(): Promise<void> {
    unsubUserDoc?.()
    await fbSignOut(auth)
    try {
      await terminate(db)
      await clearIndexedDbPersistence(db)
    } finally {
      location.assign('/')
    }
  }

  return {
    user,
    role,
    displayName,
    ready,
    isSignedIn,
    isPending,
    isMember,
    isEditor,
    isSuperadmin,
    initial,
    hasRole,
    init,
    whenReady: () => readyPromise,
    settled,
    refreshRole: () => loadClaims(true),
    signInWithGoogle,
    signInWithEmail,
    signUp,
    resetPassword,
    signOut,
  }
})
